import { useEffect, useState, useCallback, useMemo, Key } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  clinicsStatus,
  getClinics,
  selectClinics,
} from '../../../store/reducers/ClinicSlice';
import {
  getMedVoxTasks,
  medVoxTaskStatus,
  MedVoxTaskRequest,
  selectRoboCallTasks,
  totalRoboCallTasks,
} from '../../../store/reducers/MedVoxTaskSlice';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { RESULTS_MAP, STATUS_MAP } from './constants';
import { formatDate, formatDateTime } from '../../../utils';
import { AudioSrc, RangeValue } from './types';
import { MedVoxTask } from '../../../models/MedVox/MedVoxTask';

const PAGE_SIZE: number = 20;

export const useMedVoxTasks = () => {
  const tasks = useAppSelector(selectRoboCallTasks);
  const clinics = useAppSelector(selectClinics);
  const clinicStatus = useAppSelector(clinicsStatus);
  const taskStatus = useAppSelector(medVoxTaskStatus);
  const totalCount = useAppSelector(totalRoboCallTasks);
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly Key[]>([]);
  const [index, setIndex] = useState<number>();
  const [audioSrc, setAudioSrc] = useState<AudioSrc>();
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [taskRequest, setTaskRequest] = useState<MedVoxTaskRequest>({
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    pageNumber: 0,
    pageSize: pageSize,
  } as MedVoxTaskRequest);

  let count = 0;

  const currentTasks = useMemo(
    () =>
      tasks.map((task) => ({
        id: task.id,
        surname:
          (task.surname ? task.surname + ' ' : '') +
          (task.givenName ? task.givenName + ' ' : '') +
          (task.patronym ? task.patronym : ''),
        phone: task.phone,
        results: RESULTS_MAP[task.state],
        status: task.callAttempts[task.callAttempts.length - 1]
          ? STATUS_MAP[
              task.callAttempts[task.callAttempts.length - 1].callAttemptState
            ]
          : null,
        lastTime: task.callAttempts[task.callAttempts.length - 1]
          ? formatDateTime(
              task.callAttempts[task.callAttempts.length - 1].callDateTime,
            )
          : null,
        calls: task.callAttempts.map((item) => ({
          id: item.id,
          callDateTime: item.callDateTime,
          state: STATUS_MAP[item.callAttemptState],
          fileName: item.recordingName,
        })),
        indexId: count++,
      })),
    [tasks],
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getClinics());
  }, []);

  useEffect(() => {
    dispatch(getMedVoxTasks(taskRequest));
  }, [taskRequest]);

  const handleChange = useCallback(
    (page: number, pageItemCount: number) => {
      setPageSize(pageItemCount);
      setTaskRequest({
        ...taskRequest,
        pageNumber: page - 1,
        pageSize: pageItemCount,
      });
      setExpandedRowKeys([]);
      setIndex(undefined);
    },
    [setTaskRequest, taskRequest],
  );

  const handleExpand = useCallback(
    (expanded: boolean, record: MedVoxTask) => {
      if (record.calls.length == 0) {
        return false;
      }
      const key = record.indexId;
      setIndex(key);
      if (expanded) {
        setExpandedRowKeys([...expandedRowKeys, key]);
      } else {
        setExpandedRowKeys(expandedRowKeys.filter((k) => k !== key));
      }
    },
    [setIndex, setExpandedRowKeys, expandedRowKeys],
  );

  const handleRangePicker = useCallback(
    (values: RangeValue<Dayjs>, formatString: string[]) => {
      formatString &&
        setTaskRequest({
          ...taskRequest,
          startDate: formatDate(formatString[0]),
          endDate: formatDate(formatString[1]),
        });
      setExpandedRowKeys([]);
    },
    [setTaskRequest, taskRequest, setExpandedRowKeys],
  );

  const handleCallStatus = useCallback(
    (e: number) => {
      setTaskRequest({ ...taskRequest, state: e });
      setExpandedRowKeys([]);
    },
    [setTaskRequest, taskRequest, setExpandedRowKeys],
  );

  const handlePhone = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTaskRequest({
        ...taskRequest,
        phone: e.target.value,
      });
      setExpandedRowKeys([]);
    },
    [setTaskRequest, taskRequest, setExpandedRowKeys],
  );

  const handleClinics = useCallback(
    (e: string[]) => {
      setTaskRequest({ ...taskRequest, clinicIds: e });
      setExpandedRowKeys([]);
    },
    [setTaskRequest, taskRequest, setExpandedRowKeys],
  );

  return {
    clinicStatus,
    taskStatus,
    setTaskRequest,
    taskRequest,
    clinics,
    pageSize,
    totalCount,
    handleChange,
    currentTasks,
    expandedRowKeys,
    setExpandedRowKeys,
    handleExpand,
    audioSrc,
    handleRangePicker,
    handleCallStatus,
    handlePhone,
    handleClinics,
  };
};
